import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from './firebase';

// Save a new mark scheme to Firestore and Storage
export const saveMarkScheme = async (userId, schemeName, file) => {
  try {
    // Upload file to Firebase Storage
    const storageRef = ref(storage, `schemes/${userId}/${Date.now()}_${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Save scheme info to Firestore
    const schemeData = {
      name: schemeName,
      fileName: file.name,
      fileURL: downloadURL,
      filePath: snapshot.ref.fullPath,
      userId: userId,
      uploadDate: new Date(),
      fileSize: file.size,
      fileType: file.type
    };

    const docRef = await addDoc(collection(db, 'markSchemes'), schemeData);
    
    return {
      id: docRef.id,
      ...schemeData
    };
  } catch (error) {
    console.error('Error saving mark scheme:', error);
    throw error;
  }
};

// Get all mark schemes for a user
export const getUserMarkSchemes = async (userId) => {
  try {
    const q = query(
      collection(db, 'markSchemes'),
      where('userId', '==', userId),
      orderBy('uploadDate', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const schemes = [];
    
    querySnapshot.forEach((doc) => {
      schemes.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return schemes;
  } catch (error) {
    console.error('Error fetching mark schemes:', error);
    throw error;
  }
};

// Delete a mark scheme
export const deleteMarkScheme = async (schemeId, filePath) => {
  try {
    // Delete from Firestore
    await deleteDoc(doc(db, 'markSchemes', schemeId));
    
    // Delete file from Storage
    if (filePath) {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting mark scheme:', error);
    throw error;
  }
};

// Save user preferences to Firestore
export const saveUserPreferences = async (userId, preferences) => {
  try {
    const userPrefsRef = doc(db, 'userPreferences', userId);
    
    const preferencesData = {
      ...preferences,
      userId: userId,
      lastUpdated: new Date()
    };

    await setDoc(userPrefsRef, preferencesData, { merge: true });
    
    return preferencesData;
  } catch (error) {
    console.error('Error saving user preferences:', error);
    throw error;
  }
};

// Get user preferences from Firestore
export const getUserPreferences = async (userId) => {
  try {
    const userPrefsRef = doc(db, 'userPreferences', userId);
    const docSnap = await getDoc(userPrefsRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Return default preferences if none exist
      return {
        subject: '',
        level: '',
        ukArea: '',
        emailUpdates: false,
        productNews: false,
        researchParticipation: false
      };
    }
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    throw error;
  }
};

// Get user data (including subscription info) from Firestore
export const getUserData = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    } else {
      console.log('No user document found');
      return null;
    }
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};